/**
 * Cloudflare Images URL builder with responsive variants
 *
 * Cloudflare Images supports on-the-fly resizing via URL parameters when
 * "Flexible variants" is enabled (which it is for inventivehq.com).
 *
 * Benefits:
 * - Serve appropriately sized images for different devices
 * - Automatic WebP/AVIF conversion based on browser support
 * - Reduced bandwidth and faster page loads
 * - Better Core Web Vitals (LCP)
 */

// Account hash for imagedelivery.net URLs
export const CF_IMAGES_ACCOUNT_HASH = 'GtU8Bnoa3QuAEdxQ_M9RPA';
export const CF_IMAGES_BASE_URL = `https://imagedelivery.net/${CF_IMAGES_ACCOUNT_HASH}`;

export interface CloudflareImageOptions {
  width?: number;
  height?: number;
  quality?: number;
  fit?: 'scale-down' | 'contain' | 'cover' | 'crop' | 'pad';
  gravity?: 'auto' | 'center' | 'top' | 'bottom' | 'left' | 'right';
  format?: 'auto' | 'avif' | 'webp' | 'json';
  metadata?: 'keep' | 'copyright' | 'none';
  sharpen?: number;
  blur?: number;
  brightness?: number;
  contrast?: number;
}

/**
 * Extract the image ID from a Cloudflare Images URL
 *
 * Handles formats:
 * - https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/{image-id}/public
 * - https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/{image-id}/width=800
 * - {image-id} (bare ID)
 */
export function extractImageId(urlOrId: string): string | null {
  // If it's already just an ID (no slashes), return it
  if (!urlOrId.includes('/')) {
    return urlOrId;
  }

  // Check if it's a Cloudflare Images URL
  if (!urlOrId.includes('imagedelivery.net')) {
    return null;
  }

  // Extract the image ID from the URL
  // Format: https://imagedelivery.net/{account-hash}/{image-id}/{variant}
  const match = urlOrId.match(/imagedelivery\.net\/[^/]+\/([^/]+)/);
  return match ? match[1] : null;
}

/**
 * Check if a URL is a Cloudflare Images URL
 */
export function isCloudflareImageUrl(url: string): boolean {
  return url.includes('imagedelivery.net') && url.includes(CF_IMAGES_ACCOUNT_HASH);
}

/**
 * Build a Cloudflare Images URL with optional transformation parameters
 *
 * @param imageIdOrUrl - Image ID or full Cloudflare Images URL
 * @param options - Transformation options (width, height, quality, etc.)
 * @returns Transformed image URL
 *
 * @example
 * // Basic usage with ID
 * cfImage('dcb1dccb-de54-47b0-e5a8-a181e5319600', { width: 800 })
 * // => https://imagedelivery.net/.../dcb1dccb.../width=800
 *
 * @example
 * // Usage with existing URL (extracts ID and rebuilds)
 * cfImage('https://imagedelivery.net/.../abc123/public', { width: 400, quality: 85 })
 * // => https://imagedelivery.net/.../abc123/width=400,quality=85
 */
export function cfImage(imageIdOrUrl: string, options?: CloudflareImageOptions): string {
  // If no options, return public variant
  const imageId = extractImageId(imageIdOrUrl);

  if (!imageId) {
    // Not a Cloudflare image, return original URL
    return imageIdOrUrl;
  }

  const base = `${CF_IMAGES_BASE_URL}/${imageId}`;

  if (!options || Object.keys(options).length === 0) {
    return `${base}/public`;
  }

  // Build variant string from options
  const params: string[] = [];

  if (options.width) params.push(`width=${options.width}`);
  if (options.height) params.push(`height=${options.height}`);
  if (options.quality) params.push(`quality=${options.quality}`);
  if (options.fit) params.push(`fit=${options.fit}`);
  if (options.gravity) params.push(`gravity=${options.gravity}`);
  // Default to format=auto for AVIF/WebP based on browser Accept header
  params.push(`format=${options.format || 'auto'}`);
  if (options.metadata) params.push(`metadata=${options.metadata}`);
  if (options.sharpen) params.push(`sharpen=${options.sharpen}`);
  if (options.blur) params.push(`blur=${options.blur}`);
  if (options.brightness) params.push(`brightness=${options.brightness}`);
  if (options.contrast) params.push(`contrast=${options.contrast}`);

  return params.length > 0 ? `${base}/${params.join(',')}` : `${base}/public`;
}

/**
 * Predefined responsive breakpoints for common use cases
 */
export const RESPONSIVE_BREAKPOINTS = {
  // Full-screen hero background images (viewport-width)
  fullscreen: {
    mobile: { width: 480, quality: 70 }, // Reduced for faster mobile LCP
    tablet: { width: 1024, quality: 75 },
    desktop: { width: 1920, quality: 80 },
  },
  // Blog post featured images (hero)
  hero: {
    mobile: { width: 480, quality: 75 }, // Reduced for faster mobile LCP
    tablet: { width: 1024, quality: 80 },
    desktop: { width: 1366, quality: 85 },
  },
  // Blog listing card thumbnails
  thumbnail: {
    mobile: { width: 320, quality: 70 }, // Reduced for faster mobile LCP
    tablet: { width: 600, quality: 75 },
    desktop: { width: 800, quality: 80 },
  },
  // Related posts cards
  card: {
    mobile: { width: 300, quality: 80 },
    tablet: { width: 400, quality: 80 },
    desktop: { width: 500, quality: 80 },
  },
  // Inline content images in blog posts
  content: {
    mobile: { width: 480, quality: 85 },
    tablet: { width: 768, quality: 85 },
    desktop: { width: 1024, quality: 85 },
  },
} as const;

/**
 * Generate srcSet for responsive images
 *
 * @param imageIdOrUrl - Image ID or URL
 * @param preset - Predefined breakpoint preset
 * @returns srcSet string for use in <img> or next/image
 *
 * @example
 * <img
 *   src={cfImage(url, { width: 800 })}
 *   srcSet={cfImageSrcSet(url, 'thumbnail')}
 *   sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
 * />
 */
export function cfImageSrcSet(
  imageIdOrUrl: string,
  preset: keyof typeof RESPONSIVE_BREAKPOINTS
): string {
  const breakpoints = RESPONSIVE_BREAKPOINTS[preset];
  const imageId = extractImageId(imageIdOrUrl);

  if (!imageId) {
    // Not a Cloudflare image, return empty srcSet
    return '';
  }

  return [
    `${cfImage(imageId, breakpoints.mobile)} ${breakpoints.mobile.width}w`,
    `${cfImage(imageId, breakpoints.tablet)} ${breakpoints.tablet.width}w`,
    `${cfImage(imageId, breakpoints.desktop)} ${breakpoints.desktop.width}w`,
  ].join(', ');
}

/**
 * Get sizes attribute for common layouts
 */
export const SIZES = {
  // Full-screen hero backgrounds (always 100vw)
  fullscreen: '100vw',
  // Full width on mobile, constrained on larger screens
  fullWidth: '(max-width: 640px) 100vw, (max-width: 1024px) 80vw, 1200px',
  // Blog listing grid (3 columns on desktop)
  blogGrid: '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw',
  // Related posts (3 columns)
  relatedPosts: '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw',
  // Inline content images
  content: '(max-width: 640px) 100vw, (max-width: 768px) 80vw, 700px',
} as const;

/**
 * Transform a Cloudflare Images URL to use responsive parameters
 * For use in markdown/HTML content where we intercept image URLs
 *
 * @param url - Original image URL
 * @param maxWidth - Maximum width to serve (default: 1024 for content)
 * @returns Transformed URL if Cloudflare image, original otherwise
 */
export function transformToResponsive(url: string, maxWidth: number = 1024): string {
  if (!isCloudflareImageUrl(url)) {
    return url;
  }

  return cfImage(url, { width: maxWidth, quality: 85, fit: 'scale-down' });
}

/**
 * Next.js Image loader for Cloudflare Images
 * Use with the `loader` prop on next/image components
 *
 * @example
 * import { cloudflareImageLoader } from '@/lib/cloudflare-images';
 *
 * <Image
 *   loader={cloudflareImageLoader}
 *   src={featuredImage}
 *   alt="..."
 *   fill
 * />
 */
export function cloudflareImageLoader({
  src,
  width,
  quality,
}: {
  src: string;
  width: number;
  quality?: number;
}): string {
  // If not a Cloudflare image, return as-is
  if (!isCloudflareImageUrl(src)) {
    return src;
  }

  return cfImage(src, {
    width,
    quality: quality || 85,
    fit: 'scale-down',
  });
}
